import styled from '@emotion/styled';
import { Link, navigate } from '@reach/router';
import { Button } from 'components';
import colors from 'lib/colors';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScenesProgressContext } from '../ScenesProgressProvider';
import FreeGameWarningDialog from './FreeGameWarningDialog';
import { GameDifficulty } from '../../../graphql/types';

interface Props {
  state: 'freeGame' | 'unfinishedGames' | 'profile' | 'training' | 'tournament';
  difficulty: GameDifficulty;
}

const LobbyNavigation = ({ state, difficulty }: Props) => {
  const [t] = useTranslation();
  const { allScenesCompleted, tournamentScenes } = useContext(
    ScenesProgressContext,
  );
  //keep the dialog visibility in state
  const [freeGameWarningVisible, setFreeGameWarningVisible] =
    useState<boolean>(false);

  const handleFreeGameClick = async () => {
    if (!allScenesCompleted) {
      setFreeGameWarningVisible(true);
    } else {
      await navigate('/');
    }
  };

  const handleFreeGameWarningClose = () => {
    setFreeGameWarningVisible(false);
  };

  const handleFreeGameWarningContinue = async () => {
    setFreeGameWarningVisible(false);
    await navigate('/');
  };

  return (
    <Nav>
      <NavigationButton
        // @ts-ignore
        linkActive={state === 'freeGame'}
        label={t('lobby.nav.play', 'Hrát')}
        onClick={handleFreeGameClick}
      />
      <NavigationButton
        // @ts-ignore
        as={Link}
        linkActive={state === 'training'}
        label={t('lobby.nav.training', 'Moje školení')}
        to="/training"
      />
      {difficulty === GameDifficulty.MEDIUM && tournamentScenes.length !== 0 && (
        <NavigationButton
          // @ts-ignore
          as={Link}
          linkActive={state === 'tournament'}
          label={t('lobby.nav.tournament', 'Turnaj')}
          to="/tournament"
        />
      )}
      <NavigationButton
        // @ts-ignore
        as={Link}
        linkActive={state === 'unfinishedGames'}
        label={t('lobby.nav.unfinishedGames', 'Rozehrané')}
        to="/unfinished-games"
      />
      <FreeGameWarningDialog
        isOpen={freeGameWarningVisible}
        onClose={handleFreeGameWarningClose}
        onContinue={handleFreeGameWarningContinue}
      />
    </Nav>
  );
};

export default LobbyNavigation;

const Nav = styled('nav')({
  marginTop: 16,
  display: 'flex',
  alignSelf: 'center',
  backgroundColor: colors.darkBlue,
  borderRadius: 6,
  '@media screen and (max-width: 1000px)': {
    marginTop: 8,
  },
});

interface NavigationButtonProps {
  linkActive: boolean;
}

const NavigationButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'linkActive',
})<NavigationButtonProps>(({ linkActive }) => ({
  fontStyle: 'normal',
  fontWeight: 'bold',
  height: ' 100%',
  background: 'none',
  padding: '1em 3em',
  whiteSpace: 'nowrap',
  color: linkActive ? colors.white : colors.lobbyText,
  backgroundColor: linkActive ? colors.employee : 'transparent',
  textDecoration: 'none',
  ':focus': {
    boxShadow: 'none',
  },
  ':hover': {
    filter: 'brightness(125%)',
  },
}));
